import { useEffect, useId } from "react";

import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";

export const useTransactionsLiveCountsSubscription = ({
  query = {},
}: { query?: ServerFilters }) => {
  const { tenantId } = useTenant();
  const id = useId();

  const {
    data: liveCounts,
    loading,
    error,
    subscribeToMore,
  } = useQuery(TRANSACTIONS_LIVE_COUNTS, {
    variables: {
      tenantId,
      query,
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (!tenantId) return;

    const unsubscribe = subscribeToMore({
      document: TRANSACTIONS_LIVE_COUNTS_SUBSCRIPTION,
      variables: {
        tenantId,
        sessionId: id,
        query,
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const liveCounts = subscriptionData.data.transactionsLiveCounts;

        return {
          ...prev,
          transactionsCounts: liveCounts,
        };
      },
    });

    return unsubscribe;
  }, [tenantId, id, query, subscribeToMore]);

  const isLoading = loading && !liveCounts;

  return {
    liveCounts: Number.parseInt(liveCounts?.transactionsCounts ?? "0"),
    loading: isLoading,
    error,
  };
};

const TRANSACTIONS_LIVE_COUNTS_SUBSCRIPTION = gql`
  subscription TransactionsLiveCounts($tenantId: String, $sessionId: String, $query: JSON) {
    transactionsLiveCounts(tenantId: $tenantId, sessionId: $sessionId, query: $query)
  }
`;

const TRANSACTIONS_LIVE_COUNTS = gql`
  query TransactionsLiveCounts($tenantId: String, $query: JSON) {
    transactionsCounts(tenantId: $tenantId, query: $query)
  }
`;
